import { useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import type { ToolType } from '../types/dashboard';
import {
  getToolPermissionConfig,
  getPermissionConfig,
  UPGRADE_URL,
  type ToolPermissionConfig,
  type PermissionConfig,
} from '../config/permissions';

export interface ToolAccessInfo {
  hasAccess: boolean;
  requiredPermission?: PermissionConfig;
  upgradeUrl: string;
  toolConfig: ToolPermissionConfig | undefined;
}

export function usePermissions() {
  const { user } = useAuth();
  const manualPermissions = user?.manual_permissions || [];
  const stripeMetadata = user?.stripe_metadata || []; // Access tiers ('steamroom', 'zeroday', 'wsjonly')

  // Combined permissions from all sources
  const allPermissions = useMemo(
    () => [...new Set([...manualPermissions, ...stripeMetadata])],
    [manualPermissions, stripeMetadata]
  );

  // Check if user has a specific permission (from either source)
  const hasPermission = useMemo(
    () => (permissionId: string): boolean => {
      return allPermissions.includes(permissionId);
    },
    [allPermissions]
  );

  // Check if user has any of the required permissions (supports string or array)
  const hasAnyPermission = useMemo(
    () => (permissions: string | string[]): boolean => {
      if (Array.isArray(permissions)) {
        return permissions.some((p) => allPermissions.includes(p));
      }
      return allPermissions.includes(permissions);
    },
    [allPermissions]
  );

  // Check if user has access to a specific tool
  const hasToolAccess = useMemo(
    () => (toolType: ToolType): boolean => {
      const toolConfig = getToolPermissionConfig(toolType);

      // If tool has no required permission, it's free for all
      if (!toolConfig?.requiredPermission) {
        return true;
      }

      // Check if user has any of the required permissions
      return hasAnyPermission(toolConfig.requiredPermission);
    },
    [hasAnyPermission]
  );

  // Get detailed access information for a tool
  const getToolAccessInfo = useMemo(
    () => (toolType: ToolType): ToolAccessInfo => {
      const toolConfig = getToolPermissionConfig(toolType);
      const requiredPermissions = toolConfig?.requiredPermission;

      if (!requiredPermissions) {
        // Tool is free for all
        return {
          hasAccess: true,
          toolConfig,
          upgradeUrl: UPGRADE_URL,
        };
      }

      const hasAccess = hasAnyPermission(requiredPermissions);
      // For display purposes, show the first permission in the array
      const firstPermissionId = Array.isArray(requiredPermissions)
        ? requiredPermissions[0]
        : requiredPermissions;
      const requiredPermission = getPermissionConfig(firstPermissionId);

      return {
        hasAccess,
        requiredPermission,
        toolConfig,
        upgradeUrl: UPGRADE_URL,
      };
    },
    [hasAnyPermission]
  );

  // Check if a tool should be visible in the Add Tool modal
  // Admin-only tools are completely hidden from users without permission
  const isToolVisible = useMemo(
    () => (toolType: ToolType): boolean => {
      const toolConfig = getToolPermissionConfig(toolType);

      // If not adminOnly, always visible (may show locked if no access)
      if (!toolConfig?.adminOnly) {
        return true;
      }

      // adminOnly tools: only visible if user has any of the required permissions
      if (toolConfig.requiredPermission) {
        return hasAnyPermission(toolConfig.requiredPermission);
      }

      // adminOnly with no requiredPermission = visible only to users who hold
      // the admin_only manual permission (pre-alpha internal previews)
      return allPermissions.includes('admin_only');
    },
    [hasAnyPermission, allPermissions]
  );

  return {
    manualPermissions,
    stripeMetadata,
    allPermissions,
    hasPermission,
    hasAnyPermission,
    hasToolAccess,
    getToolAccessInfo,
    isToolVisible,
  };
}
